import React, { useEffect, useState, useContext } from 'react';
import { Avatar, Box, VStack, Text, Button, Flex, IconButton, useToast } from '@chakra-ui/react';
import { FiChevronLeft } from 'react-icons/fi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toDate } from '../../../utils/dateUtils';
import { StickyCtaBar } from '../../../components/ui';
import { openCrispChat } from '../../../components/FrostedSupportButton';
import { collabApi } from '../../../services/api/collab';
import { CollabDataContext } from '../../../contexts/collabDataContext';
import { InfluencerCampaignDataContext } from '../../../contexts/InfluencerCampaignDataContext';
import { InfluencerDataContext } from '../../../contexts/InfluencerDataContext';
import { ClientDataContext } from 'contexts/ClientDataContext';
import { getInfluencerDisplayName } from '../../../utils/influencerUtils';
import { ff } from '../../../theme';
import { formatEarningsShort } from '../../../utils/earningsUtils';
import { useProductSelectionGuard } from '../utils/useProductSelectionGuard';

const ViewRewardPage: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    
    const [searchParams] = useSearchParams();
    const id = searchParams.get('id');

    const collabData = useContext(CollabDataContext);
    const campaignData = useContext(InfluencerCampaignDataContext);
    const { influencerData } = useContext(InfluencerDataContext);
    const clients = useContext(ClientDataContext);
    const collab = collabData.find(c => c.id === id);
    const campaign = id ? campaignData[id] : undefined;
  // A brand can add an optional product to a live campaign; the endpoint below refuses a collab
  // that owes a selection. Send the creator to make one instead of failing with a 400.
  const goToProductSelection = useProductSelectionGuard(collab, campaign);
    const client = clients[campaign?.clientID];

    const [timeLeft, setTimeLeft] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

  // A reopened tab can outlive the reward: once the collab has moved on, "Complete Reward" would
  // only try to reopen it, so send the creator to the collab itself. Not while our own submit is in
  // flight: its success moves the status too, and handleCompleteReward does that navigation.
  useEffect(() => {
    if (collab && collab.status !== 'collabRedeemed' && !isLoading) {
      navigate(`/collabs/collab-details/${collab.id}`, { replace: true });
    }
  }, [collab, isLoading, navigate]);

  useEffect(() => {
    if (!collab || !collab.collabRedeemedTime) return;

    const calculateRemainingTime = (collab) => {
      const now = new Date();
      const redeemedAt = collab.collabRedeemedTime.toDate();
      const expirationTime = new Date(redeemedAt.getTime() + 24 * 60 * 60 * 1000); // 24 hours after redemption
      const remainingTime = expirationTime.getTime() - now.getTime();
      return Math.max(0, Math.floor(remainingTime / 1000)); // Convert to seconds
    };

    const timer = setInterval(() => {
      const remainingTime = calculateRemainingTime(collab);
      setTimeLeft(remainingTime);

      if (remainingTime <= 0) {
        clearInterval(timer);
        // navigate('/collabs');
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [collab, navigate]);

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleCompleteReward = async () => {
    setIsLoading(true);
    try {
      if (!id) throw new Error("Collab ID is missing");
      const requireDraftApproval = collab?.content && Object.keys(collab.content).length > 0 ? false : campaign?.requireDraftApproval;
      if (goToProductSelection()) return;
      await collabApi.advanceCollabToContentSubmission({
        collabId: collab.id,
        skipRedeem: false,
        requireDraftApproval
      });
      toast({
        title: "Reward Completed!",
        description: "Your reward has been marked as completed.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      navigate('/collabs');
    } catch (error) {
      console.error('Error completing reward:', error);
      toast({
        title: "Error",
        description: "There was an error completing your reward. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/collabs");
  };

  if (!collab || !campaign) {
    return <Box>Loading...</Box>;
  }

  const creditText = formatEarningsShort(collab.credit);
  const heroSize = creditText.length <= 4 ? '80px' : creditText.length <= 6 ? '60px' : '44px';
  const redeemed = toDate(collab.collabRedeemedTime);
  const redeemedLine = redeemed
    ? `Redeemed ${redeemed.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} · ${redeemed.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
    : null;
  const barWidth = `${Math.min(100, (timeLeft / 86400) * 100)}%`;
  const unit = { fontSize: '11px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase' as const, color: '#B4B4B8', lineHeight: 'normal' };

  return (
    <Box minH="100svh" bg="white" color={ff.ink} fontFamily="display">
      <Box maxW={{ base: '560px', lg: '720px' }} mx="auto" pb="170px">
        <Flex align="center" gap="8px" pt="max(12px, env(safe-area-inset-top))" px="12px">
          <IconButton
            aria-label="Go back"
            icon={<FiChevronLeft size={22} strokeWidth={2.4} />}
            onClick={handleBack}
            variant="unstyled"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flex="none"
            w="44px"
            minW="44px"
            h="44px"
            borderRadius="999px"
            color={ff.ink}
          />
          <Text flex={1} fontSize="13px" fontWeight={600} letterSpacing="0.08em" textTransform="uppercase" color="#55555A" lineHeight="normal">
            Your reward
          </Text>
          <Button
            onClick={openCrispChat}
            aria-label="Support"
            variant="unstyled"
            display="flex"
            alignItems="center"
            flex="none"
            h="44px"
            px="14px"
            borderRadius="999px"
            border="1.5px solid"
            borderColor={ff.ink}
            fontSize="14px"
            fontWeight={600}
            color={ff.ink}
          >
            Support
          </Button>
        </Flex>

        <VStack align="stretch" spacing="16px" px="20px" pt="12px">
          <VStack as="section" aria-label="Time left" align="stretch" spacing="10px" p="20px" borderRadius="24px" bg={ff.ink}>
            <Flex align="center" justify="space-between" gap="10px">
              <Text fontSize="13px" fontWeight={600} letterSpacing="0.1em" textTransform="uppercase" color="#B4B4B8" lineHeight="normal">
                Reward live for
              </Text>
              <Flex flex="none" h="28px" px="10px" borderRadius="999px" align="center" bg={ff.lime} color={ff.ink} fontSize="12px" fontWeight={600}>
                Active
              </Flex>
            </Flex>
            <Text
              role="timer"
              aria-label="Time left on your reward"
              textStyle="display"
              fontSize="53px"
              lineHeight={0.85}
              letterSpacing="0.01em"
              color={ff.lime}
              whiteSpace="nowrap"
              sx={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {formatTime(timeLeft)}
            </Text>
            <Flex gap="28px">
              <Text {...unit}>Hours</Text>
              <Text {...unit}>Min</Text>
              <Text {...unit}>Sec</Text>
            </Flex>
            <Box aria-hidden="true" h="8px" borderRadius="999px" bg="#2A2A2C" overflow="hidden">
              <Box h="100%" w={barWidth} borderRadius="999px" bg={ff.lime} />
            </Box>
          </VStack>

          <VStack as="section" aria-label="Reward" align="stretch" spacing="8px" p="20px" borderRadius="24px" border="2px solid" borderColor={ff.ink}>
            <Text fontSize="13px" fontWeight={600} letterSpacing="0.1em" textTransform="uppercase" color="#55555A" lineHeight="normal">
              In-app credit
            </Text>
            <Flex align="flex-end" gap="12px" minW={0}>
              <Text textStyle="display" fontSize={heroSize} lineHeight={0.85} textTransform="none" whiteSpace="nowrap">{creditText}</Text>
              <Text textStyle="display" fontSize="22px" lineHeight={1.12} pb="6px">FoodFluence credit</Text>
            </Flex>
            <Text fontSize="16px" lineHeight="1.4" color={ff.body}>
              Show this screen to the staff at {client?.accountName} to redeem your reward. Only redeemable once.
            </Text>
          </VStack>

          <Flex align="center" gap="14px" p="14px" borderRadius="18px" bg={ff.grey}>
            <Avatar
              src={client?.instagramProfilePicture}
              name={client?.accountName}
              boxSize="52px"
              borderRadius="14px"
              bg="#E3E8EF"
              color={ff.ink}
              flex="none"
              sx={{ '& > [role=img]': { fontSize: '17px', fontWeight: 600 }, img: { borderRadius: '14px' } }}
            />
            <VStack align="stretch" spacing="3px" flex={1} minW={0}>
              <Text fontSize="19px" fontWeight={600} lineHeight="normal">{client?.accountName}</Text>
              <Text fontSize="14px" lineHeight="normal" color={ff.body}>{getInfluencerDisplayName(influencerData)}</Text>
              {redeemedLine && <Text fontSize="14px" lineHeight="normal" color={ff.body}>{redeemedLine}</Text>}
            </VStack>
          </Flex>
        </VStack>
      </Box>

      <StickyCtaBar noNav>
        <Button
          variant="lime"
          w="100%"
          h="56px"
          borderRadius="999px"
          fontSize="17px"
          fontWeight={600}
          onClick={handleCompleteReward}
          isLoading={isLoading}
          loadingText="Completing..."
          aria-label="Complete reward"
        >
          Complete Reward
        </Button>
      </StickyCtaBar>
    </Box>
  );
};

export default ViewRewardPage;
